import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { isNativeIOS } from "../lib/platform";
import { restorePurchases } from "../lib/revenuecat";
import { useBilling } from "../contexts/BillingContext";

// Membership card — rendered inside Settings. Pulls the user's current
// entitlement from /api/billing/status and presents it in editorial style.
//
// App Store §3.1.1 / §3.1.3 ("anti-steering") compliance:
//   • On native iOS we must never link to bilateralcalm.com for subscribing
//     or managing the plan. Upgrades flow through StoreKit (RevenueCat) via
//     /pricing; subscription management opens Apple's subscriptions page.
//   • On web (Capacitor.getPlatform() !== "ios") we keep the existing
//     website-driven Stripe flow.

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const APPLE_SUBS_URL = "https://apps.apple.com/account/subscriptions";

// User-facing copy per subscription state. `iosNative` toggles wording
// so we never mention "the website" on iOS (Apple anti-steering rule).
function viewModelFor(status, expiresAt, planId, iosNative) {
  const expiresDate = expiresAt ? new Date(expiresAt) : null;
  const daysLeft = expiresDate
    ? Math.max(0, Math.ceil((expiresDate - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  if (status === "waived") {
    return {
      eyebrow: "Membership · comp",
      headline: "Your access is on us.",
      body: expiresDate
        ? `Comp access through ${expiresDate.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}. No action needed.`
        : "Your account has permanent, complimentary access to the full library. No action needed.",
      showManageBtn: false,
      showUpgradeBtn: false,
    };
  }

  if (status === "trialing") {
    return {
      eyebrow: "Membership · free trial",
      headline: daysLeft === 1 ? "One free day left." : `${daysLeft} free days left.`,
      body: iosNative
        ? "The full library is open during your trial. Tap below to choose a plan and continue."
        : "The full library is open during your trial. Head to the website when you're ready to continue.",
      showManageBtn: false,
      showUpgradeBtn: true,
    };
  }

  if (status === "active") {
    const planLabel = planId === "annual" ? "Annual plan" : planId === "monthly" ? "Monthly plan" : "Plan";
    return {
      eyebrow: `Membership · ${planLabel.toLowerCase()}`,
      headline: planId === "annual" ? "You're on the Annual plan." : "You're on the Monthly plan.",
      body: expiresDate
        ? `Next renewal on ${expiresDate.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}.`
        : "Your membership is active.",
      showManageBtn: true,
      showUpgradeBtn: false,
    };
  }

  // expired / unknown
  return {
    eyebrow: "Membership · expired",
    headline: "Your free trial has ended.",
    body: iosNative
      ? "Choose a plan to keep your practices and pick up where you left off."
      : "Subscribe on the website to keep your practices and pick up where you left off.",
    showManageBtn: false,
    showUpgradeBtn: true,
  };
}

const MembershipCard = () => {
  // Read billing from the shared BillingContext (fetched once at
  // sign-in). We keep a local `restoreOverride` state so the iOS
  // Restore Purchases flow can update the card in place after
  // RevenueCat unlocks an entitlement, without a full page refetch.
  const { billing: contextBilling, loading, refresh } = useBilling();
  const [restoreOverride, setRestoreOverride] = useState(null);
  const billing = restoreOverride || contextBilling;
  const [restoring, setRestoring] = useState(false);

  if (loading || !billing || billing.status == null) return null;

  const iosNative = isNativeIOS();
  const vm = viewModelFor(billing.status, billing.expires_at, billing.plan_id, iosNative);
  const websiteOrigin = window.location.origin;

  // CTA targets diverge by platform:
  //   iOS upgrade → /pricing (PricingPage hands off to RevenueCat StoreKit)
  //   iOS manage  → Apple subscriptions page
  //   Web both    → bilateralcalm.com/pricing (Stripe)
  const upgradeHref = iosNative ? "/pricing" : `${websiteOrigin}/pricing`;
  const manageHref = iosNative ? APPLE_SUBS_URL : `${websiteOrigin}/pricing`;
  // Native iOS uses in-app routing for the upgrade button; only the manage
  // button (which deep-links into Apple's subscription manager) opens
  // outside the app.
  const upgradeTarget = iosNative ? undefined : "_blank";
  // Both native iOS (deep-link out to App Store subscriptions) and web
  // (open bilateralcalm.com pricing) open in a new context.
  const manageTarget = "_blank";

  const upgradeLabel = iosNative ? "Choose your plan" : "Manage at bilateralcalm.com";
  const manageLabel = iosNative ? "Manage subscription" : "Manage at bilateralcalm.com";
  const footnote = iosNative
    ? "Cancel anytime from Manage subscription above."
    : "Cancel anytime from your account on the website.";

  return (
    <div
      className="rounded-3xl border border-[#E5DCC9] bg-[#FBF8F1] p-8 sm:p-12 mb-6"
      data-testid="membership-card"
    >
      <span
        className="inline-flex items-center px-4 py-1.5 rounded-full bg-[#3090A8]/15 text-[#6B8668] text-xs uppercase tracking-[0.18em] font-semibold mb-6"
        data-testid="membership-eyebrow"
      >
        {vm.eyebrow}
      </span>
      <h2
        className="font-outfit font-semibold text-3xl sm:text-4xl lg:text-5xl text-[#1B2349] tracking-tight mb-5 leading-[1.1]"
        data-testid="membership-headline"
      >
        {vm.headline}
      </h2>
      <p
        className="text-lg text-[#4A5568] leading-relaxed max-w-2xl mb-8"
        data-testid="membership-body"
      >
        {vm.body}
      </p>

      {vm.showUpgradeBtn && (
        <a
          href={upgradeHref}
          target={upgradeTarget}
          rel={upgradeTarget ? "noopener noreferrer" : undefined}
          className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#1B2349] text-white font-medium hover:bg-[#1A202C] transition-colors duration-200"
          data-testid="membership-upgrade-btn"
        >
          {upgradeLabel}
          <ArrowUpRight className="w-4 h-4" />
        </a>
      )}

      {vm.showManageBtn && (
        <a
          href={manageHref}
          target={manageTarget}
          rel={manageTarget ? "noopener noreferrer" : undefined}
          className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-[#1B2349]/15 text-[#1B2349] font-medium hover:bg-white/60 transition-colors duration-200"
          data-testid="membership-manage-btn"
        >
          {manageLabel}
          <ArrowUpRight className="w-4 h-4" />
        </a>
      )}

      {/* iOS-only Restore Purchases button — App Store Guideline 3.1.1
          requires apps with IAP to provide a restore flow. Calls
          RevenueCat's StoreKit restore which is the official mechanism. */}
      {iosNative && (
        <button
          type="button"
          onClick={async () => {
            if (restoring) return;
            setRestoring(true);
            try {
              const result = await restorePurchases();
              const ent = result?.customerInfo?.entitlements?.active;
              if (ent && Object.keys(ent).length > 0) {
                toast.success("Purchases restored. Your subscription is active.");
                // Refresh the shared BillingContext so every consumer (this
                // card + the Dashboard + the paywall guard) picks up the
                // new entitlement.
                try { await refresh(); } catch { /* non-fatal */ }
                // Also swap in a fresh local copy so the card updates
                // immediately even before the context effect runs.
                const sessionToken = localStorage.getItem("session_token");
                if (sessionToken) {
                  try {
                    const fresh = await axios.get(`${API}/billing/status`, {
                      headers: { Authorization: `Bearer ${sessionToken}` },
                    });
                    setRestoreOverride(fresh.data);
                  } catch { /* non-fatal */ }
                }
              } else {
                toast("No prior purchases found on this Apple ID.");
              }
            } catch (err) {
              // RC returns userCancelled = true when the user dismisses
              // the system "restore" sheet. Don't show an error toast for
              // that case.
              if (!err?.userCancelled) {
                toast.error("Couldn't restore purchases. Please try again.");
              }
            } finally {
              setRestoring(false);
            }
          }}
          disabled={restoring}
          className="ml-3 inline-flex items-center gap-2 px-5 py-3 rounded-full text-[#1B2349] font-medium hover:bg-white/60 transition-colors duration-200 disabled:opacity-60"
          data-testid="membership-restore-btn"
        >
          {restoring && <Loader2 className="w-4 h-4 animate-spin" />}
          Restore purchases
        </button>
      )}

      <p className="mt-5 text-sm text-[#718096]" data-testid="membership-footnote">
        {footnote}
      </p>
    </div>
  );
};

export default MembershipCard;
